import * as React from 'react';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { Box, Typography } from '@mui/material';
import DistributionTable from './table';

const pieParams = {
  height: 200,
  margin: { left: 100 },
};

const palette = [
  '#3A5B8E', '#5A85C2', '#9EBBD7', '#6AA6A3',
  '#8AB8B1', '#A4BEB3', '#C0CEC5', '#4A4A4A',
  '#7A7A7A', '#A5A5A5', '#C2C2C2', '#D8D8D8',
];

const safeParse = (raw) => {
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const DistributionPie = ({ data, category, value, section, txtBlock, title }) => {
  const sortedChartData = React.useMemo(() => {
    const parsed = safeParse(data);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    return parsed
      .map((item, index) => ({
        id: index,
        value: Number(item?.pct) || 0,
        label: item?.[value],
      }))
      .sort((a, b) => b.value - a.value);
  }, [data, value]);

  const description = React.useMemo(() => {
    const parsed = safeParse(txtBlock);
    if (!Array.isArray(parsed)) return null;
    return parsed.find((d) => d?.section === section) ?? null;
  }, [txtBlock, section]);

  const total = React.useMemo(
    () => sortedChartData.reduce((a, b) => a + b.value, 0),
    [sortedChartData],
  );

  const getArcLabel = React.useCallback(
    (params) => (total > 0 ? `${((params.value / total) * 100).toFixed(0)}%` : ''),
    [total],
  );

  if (data === undefined || data === null) {
    return <Box role="status">No data available.</Box>;
  }

  if (sortedChartData.length === 0) {
    return null;
  }

  return (
    <Box component="div" className="option-title my-1 py-0">
      <Typography
        className="Item-Head"
        variant="h3"
        sx={{
          color: '#333',
          fontWeight: 700,
          fontSize: '1rem',
          pb: '1rem !important',
          display: { md: 'block', sm: 'none', xs: 'none' },
        }}
      >
        {title}
      </Typography>
      <Box component="div" sx={{ background: '#fbfbfc', pt: 1 }}>
        {description?.text ? (
          <Box component="div" className="option-content" sx={{ px: 2 }}>
            <Typography
              variant="body2"
              component="p"
              sx={{ fontSize: '.9rem', mb: 1 }}
              dangerouslySetInnerHTML={{ __html: description.text }}
            />
          </Box>
        ) : null}
        <PieChart
          colors={palette}
          slotProps={{
            legend: {
              direction: 'horizontal',
              position: { vertical: 'bottom', horizontal: 'center' },
            },
          }}
          series={[
            {
              data: sortedChartData,
              highlightScope: { faded: 'global', highlighted: 'item' },
              arcLabel: getArcLabel,
              arcLabelMinAngle: 35,
              // faded: { innerRadius: 7, additionalRadius: -17, color: 'gray' },
              faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },

            },
          ]}
          sx={{
            [`& .${pieArcLabelClasses.root}`]: {
              fill: 'white',
              fontWeight: 'bold',
              fontSize: 14,
            },
          }}
          {...pieParams}
        />
        <DistributionTable metrics={sortedChartData} category={category} />
      </Box>
    </Box>
  );
};

export default DistributionPie;
